import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronRight, UserRound, LogOut } from 'lucide-react';
import { BRAND } from '@/lib/brand';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/products', label: 'Products' },
  { to: '/events', label: 'Events' }
];

export const Header = ({ variant = 'transparent' }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const accountPath = user?.role === 'admin' ? '/dashboard' : '/portal';

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const onLanding = pathname === '/';
  const solid = scrolled || !onLanding || variant === 'solid';

  return (
    <header
      className={cn(
        'inset-x-0 top-0 z-50 transition-all duration-300',
        solid
          ? 'fixed bg-background/85 backdrop-blur-xl border-b border-border/60'
          : 'absolute bg-transparent'
      )}
    >
      <div className={cn('container mx-auto flex items-center justify-between transition-all duration-300', solid ? 'py-3' : 'py-3')}>
        <Link to="/" className="flex items-center gap-2.5 group">
          <img
            src={BRAND.logo}
            alt="VDBF"
            className={cn(
              'rounded-full ring-1 ring-border bg-card object-contain shadow-soft group-hover:scale-105 transition-all duration-300',
              solid ? 'h-11 w-11' : 'h-[11rem] w-[11rem]'
            )}
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-[15px] font-semibold text-foreground tracking-tight">Vegan Danish</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Bakery Foods&trade;</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                cn(
                  'px-3.5 py-2 text-sm font-medium rounded-full transition-colors',
                  isActive
                    ? 'text-primary bg-secondary/70'
                    : 'text-foreground/75 hover:text-primary hover:bg-secondary/50'
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex text-foreground/80 hover:text-primary">
            <Link to="/products">Order sheet</Link>
          </Button>
          {user ? (
            <>
              <Button asChild size="sm" className="hidden sm:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4" data-testid="header-account-link">
                <Link to={accountPath}>
                  <UserRound className="mr-1.5 h-4 w-4" /> {user.role === 'admin' ? 'Dashboard' : 'My orders'}
                </Link>
              </Button>
              <Button variant="ghost" size="icon" onClick={onLogout} className="hidden sm:inline-flex text-foreground/70 hover:text-primary" aria-label="Log out" data-testid="header-logout-button">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex rounded-full px-4 border-primary/25 text-primary hover:bg-secondary" data-testid="header-login-link">
                <Link to="/login">Log in</Link>
              </Button>
              <Button asChild size="sm" className="hidden sm:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4">
                <a href={`mailto:${BRAND.email}`}>
                  Contact <ChevronRight className="ml-0.5 h-4 w-4" />
                </a>
              </Button>
            </>
          )}
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 rounded-full hover:bg-secondary text-foreground"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={cn('fixed inset-0 z-[60] lg:hidden transition-opacity', open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none')}>
        <div className="absolute inset-0 bg-primary/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
        <div className={cn('absolute right-0 top-0 h-full w-[86%] max-w-sm bg-background shadow-pop transition-transform', open ? 'translate-x-0' : 'translate-x-full')}>
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-2">
              <img src={BRAND.logo} alt="" className="h-9 w-9 rounded-full" />
              <span className="font-display font-semibold">Vegan Danish</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-secondary"><X className="h-5 w-5" /></button>
          </div>
          <div className="p-5 space-y-1">
            {NAV.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg text-foreground/85 hover:bg-secondary text-base">
                {l.label}
              </Link>
            ))}
          </div>
          <div className="p-5 border-t border-border space-y-3">
            {user ? (
              <>
                <Button asChild className="w-full bg-primary text-primary-foreground rounded-full">
                  <Link to={accountPath} onClick={() => setOpen(false)}>{user.role === 'admin' ? 'Dashboard' : 'My orders'}</Link>
                </Button>
                <Button variant="outline" className="w-full rounded-full" onClick={() => { setOpen(false); onLogout(); }}>
                  Log out
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="outline" className="w-full rounded-full">
                  <Link to="/login" onClick={() => setOpen(false)}>Log in</Link>
                </Button>
                <Button asChild className="w-full bg-primary text-primary-foreground rounded-full">
                  <a href={`mailto:${BRAND.email}`}>Contact us</a>
                </Button>
              </>
            )}
            <p className="text-xs text-muted-foreground text-center">{BRAND.email}</p>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
